import api from './api';

/**
 * When true: try real API first, fallback to mock on failure (404, network error, etc.).
 * When false: only real API, no fallback.
 */
const USE_MOCK_FALLBACK = true;
const MOCK_LATENCY_MS = 500;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Mock Data: POST /widgets/data  (keyed by instance_uuid)
// ---------------------------------------------------------------------------

const MOCK_WIDGET_DATA = {
  'w1-notes-0001': {
    data: {
      items: [
        {
          id: '1',
          title: 'Customer appointment reminder',
          description: 'Customer requested a follow-up call to discuss financing options and trade-in value.',
          dueDate: 'Mar 4',
          author: 'David Kim',
          timeAgo: '4h ago',
          metadata: { commentsCount: 7, attachmentsCount: 3, tasksCount: 2 },
        },
        {
          id: '2',
          title: 'Financing options discussion',
          description: 'Reviewed 60-month vs 72-month terms. Customer leaning toward 60-month for lower interest.',
          dueDate: 'Mar 2',
          author: 'Maria Garcia',
          timeAgo: '1d ago',
          metadata: { commentsCount: 2, attachmentsCount: 1, tasksCount: 0 },
        },
        {
          id: '3',
          title: 'Test drive scheduled',
          description: 'Test drive confirmed for Saturday 10am. SUV model preferred.',
          dueDate: 'Feb 24',
          author: 'Robert Chen',
          timeAgo: '2d ago',
          metadata: { commentsCount: 0, attachmentsCount: 0, tasksCount: 1 },
        },
        {
          id: '4',
          title: 'Comprehensive customer background and requirements',
          description: '',
          dueDate: 'Feb 20',
          author: 'David Kim',
          timeAgo: '4d ago',
          metadata: { commentsCount: 3, attachmentsCount: 2, tasksCount: 4 },
        },
      ],
    },
    meta: {
      fetched_at: '2026-03-10T10:00:00Z',
      datasource_type: 'INTERNAL_API',
      is_empty: false,
      error: null,
      pagination: null,
    },
  },

  'w2-activity-0001': {
    data: {
      items: [
        {
          id: '1',
          type: 'call',
          title: 'Title of the activity',
          description: "I reviewed the invoice and I was charged for services that weren't performed. I need this resolved immediately.",
          author: 'Robert Chen',
          date: '2026-02-20T10:00:00Z',
          timeAgo: '4h ago',
          alerts: [{ type: 'needs_attention', message: 'Needs attention: Follow-up required' }],
          metadata: {
            userId: 'usr-001',
            sentiment: 'positive',
            assignedTasks: 2,
            hasRecording: true,
            hasExternalLink: true,
            direction: 'outbound',
            duration: '4 mins',
            flagged: true,
          },
        },
        {
          id: '2',
          type: 'email',
          title: 'Email follow-up',
          description: 'Customer requested additional information about the warranty.',
          author: 'Maria Garcia',
          date: '2026-02-20T09:30:00Z',
          timeAgo: '4h ago',
          metadata: {
            userId: 'usr-002',
            commentsCount: 7,
            attachmentsCount: 3,
            assignedTasks: 2,
            hasExternalLink: true,
            flagged: true,
          },
        },
        {
          id: '3',
          type: 'call',
          title: 'Support call',
          description: 'Technical support regarding the mobile app.',
          date: '2026-02-19T14:00:00Z',
          timeAgo: '1d ago',
          alerts: [{ type: 'expired', message: 'Task expired without resolution' }],
          metadata: {
            userId: 'usr-003',
            duration: '12 mins',
            hasExternalLink: true,
            flagged: true,
          },
        },
        {
          id: '4',
          type: 'sms',
          title: 'SMS confirmation',
          description: 'Appointment confirmation sent.',
          date: '2026-02-18T11:00:00Z',
          timeAgo: '2d ago',
          metadata: {
            userId: 'usr-004',
            commentsCount: 7,
            attachmentsCount: 3,
            assignedTasks: 2,
            hasExternalLink: true,
            linkCount: 1,
            flagged: true,
          },
        },
      ],
    },
    meta: {
      fetched_at: '2026-03-10T10:00:00Z',
      datasource_type: 'INTERNAL_API',
      is_empty: false,
      error: null,
      pagination: null,
    },
  },

  // TODO: STANDARD widget mock data (TABLE, METRIC, CARDS) will be added here once Phase 6 renderers are ready
};

// Default response when there is no data
const buildEmptyResponse = (hasPagination = false) => ({
  data: hasPagination ? { rows: [] } : { items: [] },
  meta: {
    fetched_at: new Date().toISOString(),
    datasource_type: 'INTERNAL_API',
    is_empty: true,
    error: null,
    pagination: hasPagination ? { page: 1, page_size: 20, total: 0 } : null,
  },
});

// ---------------------------------------------------------------------------
// POST /widgets/data
// ---------------------------------------------------------------------------

export const fetchWidgetData = async (
  instanceUuid,
  scopeContext,
  dashboardContext,
  widgetContext = null,
  options = {},
) => {
  const { signal } = options;

  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const getMockData = async () => {
    await delay(MOCK_LATENCY_MS);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return MOCK_WIDGET_DATA[instanceUuid] ?? buildEmptyResponse();
  };

  try {
    const body = {
      instance_uuid: instanceUuid,
      scope_context: scopeContext,
      dashboard_context: dashboardContext,
    };
    if (widgetContext) body.widget_context = widgetContext;

    const response = await api.post('/v1/widgets/data', body, { signal });
    return response.data;
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    if (!USE_MOCK_FALLBACK) throw err;
    return getMockData();
  }
};
